const SITE_URL = "https://talesandlegends.org";

export function buildEventJsonLd(frontmatter, lang) {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: frontmatter.title,
    startDate: frontmatter.date,
    endDate: frontmatter.endDate || frontmatter.date,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: frontmatter.cancelled
      ? "https://schema.org/EventCancelled"
      : "https://schema.org/EventScheduled",
    location: {
      "@type": "Place",
      name: frontmatter.location,
      address: frontmatter.address && {
        "@type": "PostalAddress",
        streetAddress: frontmatter.address.street,
        postalCode: frontmatter.address.postalCode,
        addressLocality: frontmatter.address.locality,
        addressCountry: frontmatter.address.country || "DE",
      },
      geo: frontmatter.coordinates && {
        "@type": "GeoCoordinates",
        latitude: frontmatter.coordinates[0],
        longitude: frontmatter.coordinates[1],
      },
    },
    image: frontmatter.image && `${SITE_URL}${frontmatter.image}`,
    offers: buildEventOffers(frontmatter),
    inLanguage: lang,
  };
}

// One Offer per ticket type once frontmatter defines `tickets` (§ ticket
// types like SC/NPC/GSC/Solidarticket) — falls back to the single flat
// price/priceReduced offer for events that don't.
function buildEventOffers(frontmatter) {
  const availability = frontmatter.registrationOpen ? "https://schema.org/InStock" : "https://schema.org/SoldOut";

  if (frontmatter.tickets?.length > 0) {
    return frontmatter.tickets.map((ticket) => ({
      "@type": "Offer",
      name: ticket.label,
      price: ticket.price,
      priceCurrency: "EUR",
      availability,
      url: frontmatter.registrationUrl,
    }));
  }

  return {
    "@type": "Offer",
    price: frontmatter.price,
    priceCurrency: "EUR",
    availability,
    url: frontmatter.registrationUrl,
  };
}

export function buildDefinedTermSetJsonLd(terms, name) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name,
    hasDefinedTerm: terms.map(({ term, definition }) => ({
      "@type": "DefinedTerm",
      name: term,
      description: definition,
    })),
  };
}

/** For LARP knowledge pages that are prose, not FAQ/glossary (§10.3), and
 * for donation project pages. `datePublished`/`dateModified`/`image`/
 * `author` are omitted entirely when not given, rather than emitting an
 * invalid/empty value — `image` and `author` are Google's two most-cited
 * recommended Article properties beyond what this already sets. */
export function buildArticleJsonLd({ title, url, lang, description, image, author, datePublished, dateModified }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    url,
    inLanguage: lang,
    description,
    ...(image && { image: image.startsWith("http") ? image : `${SITE_URL}${image}` }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    author: { "@type": author ? "Person" : "Organization", name: author || "Tales&Legends gUG" },
    publisher: { "@type": "NonprofitOrganization", name: "Tales&Legends gUG" },
  };
}

export function buildFaqJsonLd(qa) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

/** Marks a hub/index page as an index rather than a standalone answer — the
 * one addition that reliably signals "this is a collection" to Google when
 * a hub's own intro copy inevitably brushes up against the same topic its
 * child pages answer in full. `items`: [{ name, url }] absolute URLs. */
export function buildCollectionPageJsonLd({ name, description, url, items }, lang) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url,
    inLanguage: lang,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: item.url,
      })),
    },
  };
}

/** items: [{ name, path }] — path relative, e.g. "/events" */
export function buildBreadcrumbJsonLd(items, lang) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}/${lang}${item.path}`,
    })),
  };
}
